import { ApiError } from '@services/apiClient'
import { logout as defaultLogout, refresh as defaultRefresh } from './authApi'
import { sessionStore } from '../services/sessionStore'

/**
 * 認証必須エンドポイント用 fetch ラッパー。
 *
 * 動作:
 *   1. sessionStore.accessToken を Authorization: Bearer に乗せて送る
 *   2. 401 を受けたら 1 度だけ /auth/refresh を呼ぶ
 *   3. refresh 成功 → sessionStore を更新して元リクエストを 1 回だけ retry
 *   4. refresh 失敗 → sessionStore.clear() + ApiError(401) を再 throw
 *
 * Phase 4 で 並行リクエスト時の refresh 重複防止 (single-flight) を追加する。
 */

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api'

let refreshInFlight: Promise<{ accessToken: string }> | null = null

export interface AuthedFetchOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  body?: unknown

  /** retry 戦略を上書きしたい場合 (テスト用) */
  refresh?: () => Promise<{ accessToken: string }>
  logout?: () => Promise<void>
}

export const authedFetch = async <T>(
  path: string,
  options: AuthedFetchOptions = {},
): Promise<T> => {
  const refreshFn = options.refresh ?? defaultRefresh
  const logoutFn = options.logout ?? defaultLogout

  const send = async (token: string): Promise<Response> => {
    const headers: Record<string, string> = {
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    }

    if (options.body !== undefined) {
      headers['Content-Type'] = 'application/json'
    }

    return fetch(`${BASE_URL}${path}`, {
      method: options.method ?? 'GET',
      credentials: 'include',
      headers,
      body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
    })
  }

  // T-C3: locked / unlocked のどちらでも accessToken は保持されているため、両状態で動かす。
  // unauth なら 401 を即 throw (caller 側で再ログインへ誘導)。
  const initial = sessionStore.getState()
  if (initial.kind === 'unauth') {
    throw new ApiError(401, 'no active session')
  }

  let res = await send(initial.accessToken)
  if (res.status === 401) {
    try {
      refreshInFlight ??= refreshFn().finally(() => {
        refreshInFlight = null
      })

      const { accessToken } = await refreshInFlight

      sessionStore.updateAccessToken(accessToken)
      res = await send(accessToken)
    } catch {
      // refresh 失敗 → クライアント側セッションも破棄して再ログインを促す
      sessionStore.clear()
      try {
        await logoutFn()
      } catch {
        // logout の失敗は無視 (cookie はサーバ側で残っても次回 refresh で 401 が返る)
      }
      throw new ApiError(401, `${options.method ?? 'GET'} ${path} failed: session expired`)
    }
  }

  if (!res.ok) {
    throw new ApiError(res.status, `${options.method ?? 'GET'} ${path} failed: ${res.status}`)
  }

  // T-D-Vault-A: 204 No Content は body を読まず undefined を返す (DELETE /vault/items/:id 用)。
  if (res.status === 204) {
    return undefined as T
  }

  return (await res.json()) as T
}
